<template>
  <header class="hm1" :class="{ 'menu-open': mobileMenuOpen }">
    <a href="/"><v-img class="logo" :src="require('../assets/logo2.png')" /></a>
    <button class="mobile-nav-toggle" type="button" :aria-expanded="mobileMenuOpen ? 'true' : 'false'" aria-label="Toggle navigation menu" @click="toggleMobileMenu">
      <span></span>
      <span></span>
      <span></span>
    </button>
    <nav class="navbar">
      <a class="button" href="/" id="nav_/" @click="closeMobileMenu">Home</a>
      <a class="button" href="/about" id="nav_/about" @click="closeMobileMenu">About</a>
      <a class="button" href="/services" id="nav_/services" @click="closeMobileMenu">Services</a>
      <a class="button" href="/contact" id="nav_/contact" @click="closeMobileMenu">Contact</a>
    </nav>
  </header>

  <section class="hero">
    <div class="hero-copy">
      <p class="eyebrow">RDS Corp Credential Verification</p>
      <p class="title">Verify internship certificates, experience letters, and issued credentials.</p>
      <p class="subtitle">Enter the credential ID shown on the bottom-right of the issued document. Most credentials issued by RDS Corp carry a 14-digit identifier.</p>
    </div>
  </section>

  <section class="verify-shell">
    <div class="search-card">
      <div class="search-head">
        <div>
          <p class="search-title">Credential Lookup</p>
          <p class="search-note">Use the exact credential ID as issued on the document to verify authenticity and holder details.</p>
        </div>
        <div class="trust-pill">
          <span class="dot"></span>
          Official RDS Corp verification record
        </div>
      </div>

      <label class="field-label" for="credential-id">Credential ID</label>
      <div class="search-row">
        <input
          id="credential-id"
          :value="searchTerm"
          class="search-input"
          type="text"
          inputmode="text"
          autocapitalize="characters"
          maxlength="32"
          placeholder="Enter credential ID"
          @input="handleCredentialInput"
          @keyup.enter="runSearch"
        >
        <button class="search-btn" :disabled="isLoading || !isSearchReady(searchTerm)" @click="runSearch">{{ isLoading ? 'Verifying...' : 'Verify Credential' }}</button>
      </div>
    </div>

    <div v-if="hasSearched && matchedCredential" class="result-card success">
      <div class="result-header">
        <div class="avatar">{{ avatarInitials(matchedCredential.fullName) }}</div>
        <div class="identity">
          <p class="result-kicker">Credential verified</p>
          <p class="result-title">{{ matchedCredential.fullName }}</p>
          <p class="result-subtitle">{{ matchedCredential.roleTitle }}</p>
        </div>
        <div class="status-chip" :class="statusChipClass(matchedCredential.status)">{{ matchedCredential.status || 'Verified' }}</div>
      </div>

      <div class="summary-panel">
        <div class="summary-item">
          <p class="label">Credential Type</p>
          <p class="value">{{ matchedCredential.credentialType }}</p>
        </div>
        <div class="summary-item">
          <p class="label">Credential ID</p>
          <p class="value">{{ matchedCredential.credentialId }}</p>
        </div>
        <div class="summary-item">
          <p class="label">Issued By</p>
          <p class="value">{{ matchedCredential.issuer || 'RDS Corp' }}</p>
        </div>
        <div class="summary-item">
          <p class="label">Department</p>
          <p class="value">{{ matchedCredential.department || 'Not specified' }}</p>
        </div>
      </div>

      <div class="grid">
        <div class="item wide" v-if="matchedCredential.roleDescription">
          <p class="label">Role Description</p>
          <p class="value">{{ matchedCredential.roleDescription }}</p>
        </div>
        <div class="item">
          <p class="label">Joined On</p>
          <p class="value">{{ formatCredentialDate(matchedCredential.joinedOn) }}</p>
        </div>
        <div class="item">
          <p class="label">Left On</p>
          <p class="value">{{ formatCredentialDate(matchedCredential.leftOn) }}</p>
        </div>
        <div class="item">
          <p class="label">Work Duration</p>
          <p class="value">{{ matchedCredential.duration || durationLabel(matchedCredential) }}</p>
        </div>
        <div class="item">
          <p class="label">Issued On</p>
          <p class="value">{{ formatCredentialDate(matchedCredential.issuedOn) }}</p>
        </div>
        <div class="item">
          <p class="label">Rating</p>
          <p class="value">{{ matchedCredential.ratingScore || 'Not rated' }}</p>
        </div>
        <div class="item">
          <p class="label">Recommendation</p>
          <p class="value">{{ matchedCredential.recommendation || 'Not specified' }}</p>
        </div>
        <div class="item wide" v-if="matchedCredential.ratingSummary">
          <p class="label">Performance Summary</p>
          <p class="value">{{ matchedCredential.ratingSummary }}</p>
        </div>
        <div class="item wide" v-if="matchedCredential.reportingManager">
          <p class="label">Reporting Manager</p>
          <p class="value">{{ matchedCredential.reportingManager }}</p>
        </div>
        <div class="item wide" v-if="matchedCredential.notes">
          <p class="label">Additional Remarks</p>
          <p class="value">{{ matchedCredential.notes }}</p>
        </div>
      </div>
    </div>

    <div v-else-if="hasSearched" class="result-card error">
      <p class="result-kicker">Credential not found</p>
      <p class="result-title">We could not verify that credential ID.</p>
      <p class="result-copy">Please confirm the ID exactly as issued on the credential. If the issue persists, contact RDS Corp for manual verification.</p>
    </div>

    <div class="confidence-card">
      <p class="confidence-title">Verification notes</p>
      <ul>
        <li>A verified result confirms that the credential record exists in the official RDS Corp verification register.</li>
        <li>The credential ID is typically printed at the bottom-right of the certificate or letter issued by RDS Corp.</li>
        <li>This page may be used by employers, institutions, and third parties for credential authenticity checks.</li>
      </ul>
    </div>
  </section>

  <footer class="fm1">
    <div class="d1">
      <div class="sd1">
        <v-img class="logo" :src="require('../assets/logo3.png')" />
      </div>
      <div class="sd2">
        <div class="s1">
          <p class="title">General</p>
          <a class="link n" href="/about">About Us</a>
          <a class="link n" href="/services">Services</a>
          <a class="link n" href="/contact">Contact Us</a>
          <p class="title">Products</p>
          <a class="link n" href="/zaqnet">Zaqnet</a>
          <a class="link n" href="/meridian">Meridian</a>
        </div>
        <div class="s1">
          <p class="title">Socials</p>
          <a class="link fb" href="https://www.facebook.com/rdscorp/" target="_blank"><span class="mdi mdi-facebook"></span> Facebook</a>
          <a class="link ig" href="https://www.instagram.com/rdscorpglobal/" target="_blank"><span class="mdi mdi-instagram"></span> Instagram</a>
          <a class="link x" href="https://www.x.com/rdscorpglobal/" target="_blank"><span class="mdi mdi-twitter"></span> Twitter</a>
          <a class="link ln" href="https://www.linkedin.com/company/rdscorp/" target="_blank"><span class="mdi mdi-linkedin"></span> LinkedIn</a>
        </div>
        <div class="s1">
          <p class="title">Terms & Policies</p>
          <a class="link n" href="/verify">Credential Verification</a>
          <a class="link n" href="/terms">Terms & Conditions</a>
          <a class="link n" href="/privacy">Privacy Policy</a>
        </div>
      </div>
    </div>
    <div class="d2">
      <p class="title">Copyright © 2026 RDS Corp. All rights reserved.</p>
    </div>
  </footer>
</template>

<script>
export default {
  name: 'VerificationPortal',
  data: () => ({
    credentials: [],
    searchTerm: '',
    hasSearched: false,
    matchedCredential: null,
    mobileMenuOpen: false,
    isLoading: false
  }),
  async mounted() {
    const pathname = window.location.pathname === '' ? '/' : window.location.pathname
    if (document.getElementById(`nav_${pathname}`)) {
      document.getElementById(`nav_${pathname}`).classList.add('selected')
    }

    await this.loadCredentials()

    const params = new URLSearchParams(window.location.search)
    const credentialId = params.get('credential_id') || params.get('credentialId') || params.get('id')
    if (credentialId) {
      this.searchTerm = credentialId
      this.runSearch()
    }
  },
  methods: {
    async loadCredentials() {
      try {
        const fallbackRecords = await this.loadLocalCredentials()
        const sheetUrl = this.credentialsSheetUrl()

        if (sheetUrl) {
          const sheetRecords = await this.loadCredentialsFromCsv(sheetUrl)
          this.credentials = this.mergeCredentials(fallbackRecords, sheetRecords)
          return
        }

        this.credentials = fallbackRecords
      } catch (error) {
        this.credentials = []
      }
    },
    credentialsSheetUrl() {
      if (process.env.VUE_APP_CREDENTIALS_SHEET_CSV_URL) {
        return this.normalizeSheetUrl(process.env.VUE_APP_CREDENTIALS_SHEET_CSV_URL)
      }

      if (!process.env.VUE_APP_CREDENTIALS_SHEET_ID) {
        return ''
      }

      const query = new URLSearchParams({ format: 'csv' })
      if (process.env.VUE_APP_CREDENTIALS_SHEET_GID) {
        query.set('gid', process.env.VUE_APP_CREDENTIALS_SHEET_GID)
      }

      return `https://docs.google.com/spreadsheets/d/${process.env.VUE_APP_CREDENTIALS_SHEET_ID}/export?${query.toString()}`
    },
    normalizeSheetUrl(value) {
      const raw = (value || '').toString().trim()
      if (!raw) {
        return ''
      }

      const idMatch = raw.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/)
      if (!idMatch) {
        return raw
      }

      const url = new URL(raw)
      const gid = url.searchParams.get('gid')
      const query = new URLSearchParams({ format: 'csv' })
      if (gid) {
        query.set('gid', gid)
      }

      return `https://docs.google.com/spreadsheets/d/${idMatch[1]}/export?${query.toString()}`
    },
    withCacheBust(url) {
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}_ts=${Date.now()}`
    },
    async loadLocalCredentials() {
      const response = await fetch(this.withCacheBust('/data/credentials.json'), { cache: 'no-store' })
      const payload = await response.json()
      return Array.isArray(payload.records) ? payload.records : []
    },
    async loadCredentialsFromCsv(url) {
      const response = await fetch(this.withCacheBust(url), { cache: 'no-store' })
      const csvText = await response.text()
      const rows = this.parseCsv(csvText)
      if (!rows.length) {
        return []
      }

      const headers = rows[0].map((header) => this.normalizeHeader(header))
      return rows
        .slice(1)
        .filter((row) => row.some((cell) => (cell || '').toString().trim() !== ''))
        .map((row) => this.mapCredentialRow(headers, row))
        .filter((record) => record.credentialId)
    },
    mergeCredentials(fallbackRecords, sheetRecords) {
      const merged = [...sheetRecords]
      const seen = new Set(sheetRecords.map((record) => this.normalize(record.credentialId)))

      fallbackRecords.forEach((record) => {
        const normalizedId = this.normalize(record.credentialId)
        if (!seen.has(normalizedId)) {
          merged.push(record)
        }
      })

      return merged
    },
    parseCsv(csvText) {
      const rows = []
      let currentRow = []
      let currentCell = ''
      let insideQuotes = false

      for (let index = 0; index < csvText.length; index += 1) {
        const character = csvText[index]
        const nextCharacter = csvText[index + 1]

        if (character === '"') {
          if (insideQuotes && nextCharacter === '"') {
            currentCell += '"'
            index += 1
          } else {
            insideQuotes = !insideQuotes
          }
          continue
        }

        if (character === ',' && !insideQuotes) {
          currentRow.push(currentCell)
          currentCell = ''
          continue
        }

        if ((character === '\n' || character === '\r') && !insideQuotes) {
          if (character === '\r' && nextCharacter === '\n') {
            index += 1
          }
          currentRow.push(currentCell)
          rows.push(currentRow)
          currentRow = []
          currentCell = ''
          continue
        }

        currentCell += character
      }

      if (currentCell.length || currentRow.length) {
        currentRow.push(currentCell)
        rows.push(currentRow)
      }

      return rows
    },
    normalizeHeader(value) {
      return (value || '')
        .toString()
        .trim()
        .replace(/^\uFEFF/, '')
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .trim()
        .toLowerCase()
    },
    rowValue(headers, row, aliases) {
      const matchIndex = headers.findIndex((header) => aliases.includes(header))
      return matchIndex >= 0 ? (row[matchIndex] || '').toString().trim() : ''
    },
    mapCredentialRow(headers, row) {
      return {
        credentialId: this.rowValue(headers, row, ['credential id', 'credentialid', 'id']),
        fullName: this.rowValue(headers, row, ['full name', 'fullname', 'name', 'candidate name']),
        credentialType: this.rowValue(headers, row, ['credential type', 'credentialtype', 'type']),
        roleTitle: this.rowValue(headers, row, ['role title', 'roletitle', 'role', 'designation']),
        roleDescription: this.rowValue(headers, row, ['role description', 'roledescription', 'description']),
        department: this.rowValue(headers, row, ['department']),
        joinedOn: this.rowValue(headers, row, ['joined on', 'joinedon', 'join date', 'start date']),
        leftOn: this.rowValue(headers, row, ['left on', 'lefton', 'end date', 'completion date']),
        duration: this.rowValue(headers, row, ['duration', 'work duration']),
        issuedOn: this.rowValue(headers, row, ['issued on', 'issuedon', 'issue date']),
        issuer: this.rowValue(headers, row, ['issued by', 'issuer']),
        status: this.rowValue(headers, row, ['status']),
        ratingScore: this.rowValue(headers, row, ['rating', 'rating score', 'ratingscore']),
        ratingSummary: this.rowValue(headers, row, ['performance summary', 'ratingsummary', 'rating summary']),
        recommendation: this.rowValue(headers, row, ['recommendation']),
        reportingManager: this.rowValue(headers, row, ['reporting manager', 'reportingmanager', 'manager']),
        notes: this.rowValue(headers, row, ['additional remarks', 'notes', 'remarks'])
      }
    },
    normalize(value) {
      return (value || '').toString().replace(/\s+/g, '').trim().toUpperCase()
    },
    isSearchReady(value) {
      const normalizedValue = this.normalize(value)
      return /^\d{14}$/.test(normalizedValue) || /^(?=.*[A-Z])[A-Z0-9-]{6,32}$/.test(normalizedValue)
    },
    handleCredentialInput(event) {
      this.searchTerm = (event.target.value || '')
        .toString()
        .toUpperCase()
        .replace(/[^A-Z0-9-]/g, '')
        .slice(0, 32)
    },
    avatarInitials(name) {
      const parts = (name || '').trim().split(/\s+/).filter(Boolean)
      return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('') || 'RD'
    },
    formatCredentialDate(value) {
      if (!value) {
        return 'Not specified'
      }
      const raw = value.toString().trim()
      const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (isoMatch) {
        const parsed = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}T00:00:00`)
        return parsed.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      }
      const compactMatch = raw.match(/^(\d{2})(\d{2})(\d{4})$/)
      if (compactMatch) {
        const parsed = new Date(`${compactMatch[3]}-${compactMatch[2]}-${compactMatch[1]}T00:00:00`)
        return parsed.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      }
      const slashedMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (slashedMatch) {
        const parsed = new Date(`${slashedMatch[3]}-${slashedMatch[2]}-${slashedMatch[1]}T00:00:00`)
        return parsed.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      }
      return raw
    },
    durationLabel(credential) {
      if (credential.duration) {
        return credential.duration
      }
      if (credential.joinedOn && credential.leftOn) {
        return `${this.formatCredentialDate(credential.joinedOn)} to ${this.formatCredentialDate(credential.leftOn)}`
      }
      return 'Not specified'
    },
    statusChipClass(status) {
      const normalizedStatus = this.normalize(status)

      if (!normalizedStatus || normalizedStatus === 'VERIFIED' || normalizedStatus === 'ACCEPTED') {
        return 'verified'
      }

      if (normalizedStatus === 'NOTACCEPTED' || normalizedStatus === 'REJECTED' || normalizedStatus === 'DECLINED') {
        return 'rejected'
      }

      if (normalizedStatus === 'PENDINGRESPONSE' || normalizedStatus === 'PENDING') {
        return 'pending'
      }

      return 'neutral'
    },
    async runSearch() {
      if (!this.isSearchReady(this.searchTerm)) {
        return
      }
      this.isLoading = true
      try {
        await this.loadCredentials()
        this.hasSearched = true
        const normalizedSearch = this.normalize(this.searchTerm)
        this.matchedCredential = this.credentials.find((credential) =>
          this.normalize(credential.credentialId) === normalizedSearch
        ) || null
        this.searchTerm = normalizedSearch

        const nextUrl = new URL(window.location.href)
        if (normalizedSearch) {
          nextUrl.searchParams.set('credential_id', normalizedSearch)
          nextUrl.searchParams.delete('credentialId')
          nextUrl.searchParams.delete('id')
        } else {
          nextUrl.searchParams.delete('credential_id')
          nextUrl.searchParams.delete('credentialId')
          nextUrl.searchParams.delete('id')
        }
        window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}`)
      } finally {
        this.isLoading = false
      }
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false
    }
  }
}
</script>

<style lang="scss">
a {
  text-decoration: none;
  color: inherit;
}

.hm1 {
  width: 100%;
  height: 150px;
  background: linear-gradient(180deg, #fff 90%, transparent 100%);
  padding: 20px 150px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;

  .logo {
    height: 80px;
    width: 80px;
    flex-grow: 0;
    flex-shrink: 0;
  }

  nav {
    flex-grow: 0;
    flex-shrink: 0;
    display: flex;
    gap: 25px;

    .button {
      background-color: rgb(229, 229, 229);
      height: 40px;
      width: auto;
      border-radius: 1000px;
      font-size: 18px;
      padding: 0 20px;
      display: grid;
      place-content: center;
    }

    .button:hover {
      outline: 2px solid #046fdb;
      outline-offset: 2px;
    }

    .button.selected {
      background-color: black;
      color: white;
    }
  }
}

.hero {
  padding: 240px 150px 50px;

  .hero-copy {
    max-width: 900px;
  }

  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: #6f6f6f;
    font-size: 14px;
    font-family: fm;
  }

  .title {
    margin-top: 24px;
    font-size: 58px;
    line-height: 1.02;
    font-family: fb;
    max-width: 900px;
  }

  .subtitle {
    margin-top: 24px;
    font-size: 26px;
    color: #626262;
    max-width: 780px;
  }
}

.verify-shell {
  padding: 0 150px 100px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.search-card,
.result-card,
.confidence-card {
  border: 1px solid #dedede;
  background-color: #fff;
  padding: 36px;
}

.search-head {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
}

.search-title,
.confidence-title,
.result-kicker,
.field-label,
.label {
  font-family: fm;
}

.search-title {
  font-size: 30px;
}

.search-note {
  margin-top: 10px;
  color: #696969;
  font-size: 17px;
}

.inline-code {
  font-family: fb;
  letter-spacing: 0.04em;
}

.trust-pill {
  border: 1px solid #d6eadc;
  background: #f3fbf5;
  color: #16653b;
  padding: 10px 16px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #169b52;
}

.field-label {
  display: block;
  margin-top: 26px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 14px;
}

.search-row {
  display: flex;
  gap: 16px;
  margin-top: 14px;
}

.search-input {
  flex: 1;
  min-height: 58px;
  border: 1px solid #c9c9c9;
  padding: 0 18px;
  font-size: 18px;
  text-transform: uppercase;
}

.search-btn {
  min-width: 200px;
  border: 0;
  background-color: #111;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;
}

.search-btn:disabled {
  background-color: #d7d9dd;
  color: #8a8f98;
  cursor: not-allowed;
  opacity: 1;
}

.result-card.success {
  border-left: 6px solid #0d8a52;
}

.result-card.error {
  border-left: 6px solid #d12d2d;
}

.result-card.error .result-kicker {
  color: #b42318;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 22px;
}

.avatar {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: linear-gradient(135deg, #111, #5f5f5f);
  color: #fff;
  display: grid;
  place-content: center;
  font-family: fb;
  font-size: 32px;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.identity {
  flex: 1;
}

.result-kicker {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #18854f;
  font-size: 13px;
}

.result-title {
  margin-top: 10px;
  font-size: 40px;
  font-family: fb;
  line-height: 1.05;
}

.result-subtitle {
  margin-top: 8px;
  font-size: 22px;
  color: #616161;
}

.status-chip {
  padding: 10px 16px;
  font-family: fm;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
}

.status-chip.verified {
  background: #eff9f2;
  color: #0c7f49;
  border: 1px solid #cde7d6;
}

.status-chip.rejected {
  background: #fef3f2;
  color: #b42318;
  border: 1px solid #f7d3cf;
}

.status-chip.pending {
  background: #fff8eb;
  color: #a15c07;
  border: 1px solid #f2ddb2;
}

.status-chip.neutral {
  background: #f4f4f5;
  color: #52525b;
  border: 1px solid #e4e4e7;
}

.summary-panel {
  margin-top: 28px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.summary-item,
.item {
  border-top: 1px solid #e5e5e5;
  padding-top: 14px;
}

.grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.item.wide {
  grid-column: 1 / -1;
}

.label {
  color: #6a6a6a;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.value {
  margin-top: 8px;
  font-size: 19px;
  line-height: 1.45;
}

.result-copy {
  margin-top: 16px;
  font-size: 18px;
  color: #5e5e5e;
}

.confidence-card {
  background: #f8f8f8;

  .confidence-title {
    font-size: 28px;
  }

  ul {
    margin-top: 18px;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-size: 18px;
  }
}

.fm1 {
  width: 100%;
  padding: 20px 150px 0;
  background-color: rgb(0, 0, 0);

  .d1 {
    display: flex;
    justify-content: space-between;

    .sd1 {
      display: flex;
      flex-direction: column;

      .logo {
        width: 250px;
        height: 250px;
      }
    }

    .sd2 {
      display: flex;
      padding-top: 30px;
      gap: 50px;

      .s1 {
        display: flex;
        flex-direction: column;
        color: #fff;
        gap: 5px;

        .title {
          color: rgb(142, 142, 142);
          font-size: 17.5px;
        }

        .link {
          font-size: 20px;
          cursor: pointer;
        }

        .link.fb:hover {
          color: #1877f2;
        }

        .link.ig:hover {
          color: #fe0b81;
        }

        .link.x:hover {
          color: #1da1f2;
        }

        .link.ln:hover {
          color: #0077b5;
        }

        .link.n:hover {
          text-decoration: underline;
        }
      }
    }
  }

  .d2 {
    width: 100%;
    padding: 10px 0;
    display: flex;

    .title {
      width: 100%;
      text-align: center;
      font-family: fl;
      color: white;
    }
  }
}

@media (max-width: 768px) and (orientation: portrait) {
  .hm1 {
    padding: 30px;
    height: 150px;

    .logo {
      width: 70px;
      height: 70px;
    }

    nav {
      display: none;
    }
  }

  .hero {
    padding: 180px 30px 40px;

    .title {
      font-size: 42px;
    }

    .subtitle {
      font-size: 22px;
    }
  }

  .verify-shell {
    padding: 0 30px 80px;
  }

  .search-card,
  .result-card,
  .confidence-card {
    padding: 24px;
  }

  .search-head,
  .result-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .search-row {
    flex-direction: column;
  }

  .search-btn {
    min-height: 54px;
    width: 100%;
    min-width: 0;
  }

  .summary-panel,
  .grid {
    grid-template-columns: 1fr;
  }

  .trust-pill,
  .status-chip {
    white-space: normal;
  }

  .fm1 {
    padding: 20px 30px 0;

    .d1 {
      flex-direction: column;

      .sd1 .logo {
        width: 200px;
        height: 200px;
      }

      .sd2 {
        flex-direction: column;
        gap: 24px;
      }
    }
  }
}
</style>
